import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/ui/Card';
import DateSelect from '../components/ui/DateSelect';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import Icons from '../components/Icons';
import AttendanceKpis from '../components/attendance/AttendanceKpis';
import SearchableChecklist from '../components/ui/SearchableChecklist';
import {
  buildAttendanceKpis,
  getAttendanceSignals,
  getStatusMeta,
  isExceptionStatus,
  isSafeguardingFlagged,
  normalizeAttendanceStatus
} from '../components/attendance/attendanceUtils';
import { useStudentStore } from '../store/StudentStore';
import { useUser } from '../store/UserContext';
import { useSettings } from '../store/SettingsContext';
import ProgrammeService from '../services/ProgrammeService';
import { formatDate } from '../utils/dateFormat';

const statusOptions = [
  { value: 'attended', label: 'Attended' },
  { value: 'late', label: 'Late' },
  { value: 'absent', label: 'Absent' },
  { value: 'unauthorised', label: 'Unauthorised' }
];

const buildCsv = (headers, rows) => {
  return [headers, ...rows]
    .map((row) =>
      row
        .map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`)
        .join(',')
    )
    .join('\n');
};

const downloadCsv = (filename, headers, rows) => {
  const csv = buildCsv(headers, rows);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const safeList = (service) => {
  if (service && typeof service.list === 'function') return service.list();
  return [];
};

export default function AttendancePage() {
  const { students, controller } = useStudentStore();
  const { user } = useUser();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const programmeService = useMemo(() => new ProgrammeService(), []);
  const [selectedProgrammeId, setSelectedProgrammeId] = useState('');
  const [sessionDate, setSessionDate] = useState(new Date().toISOString().slice(0, 10));
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [baselineRows, setBaselineRows] = useState([]);
  const [notesOpenIds, setNotesOpenIds] = useState(new Set());
  const [batchMessage, setBatchMessage] = useState('');
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [confirmBulkOpen, setConfirmBulkOpen] = useState(false);
  const [bulkMeta, setBulkMeta] = useState({ flagged: 0, unauthorised: 0 });
  const [activeKpi, setActiveKpi] = useState(null);
  const [showAllLogs, setShowAllLogs] = useState(false);
  const [showAttendedLogs, setShowAttendedLogs] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportScope, setExportScope] = useState('all');
  const [exportStudentIds, setExportStudentIds] = useState([]);
  const [exportRange, setExportRange] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 30);
    return {
      start: start.toISOString().slice(0, 10),
      end: end.toISOString().slice(0, 10)
    };
  });

  const currentUserName = user?.name || '';
  const roleLabel = `${user?.role || ''} ${user?.title || ''}`.toLowerCase();
  const isAdmin = ['manager', 'admin', 'director', 'head', 'lead'].some((token) =>
    roleLabel.includes(token)
  );

  const programmeOptions = useMemo(() => {
    const base = safeList(programmeService).map((prog) => ({
      id: prog.id,
      name: prog.name,
      day: prog.day,
      time: prog.time,
      staff: prog.staff
    }));
    if (isAdmin) {
      const seen = new Set(base.map((item) => item.name));
      const extra = students
        .flatMap((student) => student.enrolments || [])
        .map((enrol) => enrol.programme)
        .filter(Boolean)
        .filter((name) => !seen.has(name))
        .map((name, index) => ({
          id: `prog-extra-${index}`,
          name,
          day: 'TBD',
          time: 'TBD',
          staff: 'Unassigned'
        }));
      return [...base, ...extra];
    }
    const staffName = currentUserName.toLowerCase();
    return base.filter((programme) => programme.staff?.toLowerCase() === staffName);
  }, [students, programmeService, isAdmin, currentUserName]);

  const scopedStudents = useMemo(() => {
    if (isAdmin) return students;
    const programmeNames = new Set(programmeOptions.map((programme) => programme.name));
    if (programmeNames.size === 0) return [];
    return students.filter((student) =>
      (student.enrolments || []).some((enrol) => programmeNames.has(enrol.programme))
    );
  }, [students, programmeOptions, isAdmin]);

  const kpiCounts = useMemo(
    () => buildAttendanceKpis(scopedStudents, settings.attendanceThreshold),
    [scopedStudents, settings.attendanceThreshold]
  );

  useEffect(() => {
    if (!programmeOptions.length) {
      setSelectedProgrammeId('');
      return;
    }
    if (!selectedProgrammeId || !programmeOptions.find((prog) => prog.id === selectedProgrammeId)) {
      setSelectedProgrammeId(programmeOptions[0].id);
    }
  }, [programmeOptions, selectedProgrammeId]);

  const selectedProgramme = useMemo(
    () => programmeOptions.find((prog) => prog.id === selectedProgrammeId),
    [programmeOptions, selectedProgrammeId]
  );

  const enrolledStudents = useMemo(() => {
    if (!selectedProgramme) return [];
    return students.filter((student) =>
      (student.enrolments || []).some((enrol) => enrol.programme === selectedProgramme.name)
    );
  }, [students, selectedProgramme]);

  const flaggedStudentIds = useMemo(() => {
    return new Set(enrolledStudents.filter((student) => isSafeguardingFlagged(student)).map((s) => s.id));
  }, [enrolledStudents]);

  const signalsByStudentId = useMemo(() => {
    const map = new Map();
    enrolledStudents.forEach((student) => {
      const signals = getAttendanceSignals(student);
      if (signals.length) map.set(student.id, signals);
    });
    return map;
  }, [enrolledStudents]);

  useEffect(() => {
    if (!selectedProgramme) {
      setAttendanceRows([]);
      setBaselineRows([]);
      return;
    }
    const initialRows = enrolledStudents.map((student) => ({
      studentId: student.id,
      selected: true,
      status: 'attended',
      notes: ''
    }));
    setAttendanceRows(initialRows);
    setBaselineRows(initialRows);
    setNotesOpenIds(new Set());
    setBatchMessage('');
  }, [enrolledStudents, selectedProgramme]);

  const updateRow = (studentId, field, value) => {
    setAttendanceRows((prev) =>
      prev.map((row) => (row.studentId === studentId ? { ...row, [field]: value } : row))
    );
  };

  const toggleNotesOpen = (studentId) => {
    setNotesOpenIds((prev) => {
      const next = new Set(prev);
      next.add(studentId);
      return next;
    });
  };

  const selectAllRows = (value) => {
    setAttendanceRows((prev) => prev.map((row) => ({ ...row, selected: value })));
  };

  const markAllAttended = (overrideFlagged) => {
    setAttendanceRows((prev) =>
      prev.map((row) => {
        if (normalizeAttendanceStatus(row.status) === 'unauthorised') return row;
        const isFlagged = flaggedStudentIds.has(row.studentId);
        if (isFlagged && !overrideFlagged) return row;
        return { ...row, status: 'attended' };
      })
    );
  };

  const handleMarkAll = () => {
    const unauthorisedCount = attendanceRows.filter(
      (row) => normalizeAttendanceStatus(row.status) === 'unauthorised'
    ).length;
    const flaggedCount = attendanceRows.filter(
      (row) => flaggedStudentIds.has(row.studentId) && normalizeAttendanceStatus(row.status) !== 'attended'
    ).length;

    if (flaggedCount > 0) {
      setBulkMeta({ flagged: flaggedCount, unauthorised: unauthorisedCount });
      setConfirmBulkOpen(true);
      return;
    }

    markAllAttended(false);
    if (unauthorisedCount > 0) {
      setBatchMessage('Unauthorised absences were left unchanged.');
    }
  };

  const selectedCount = useMemo(
    () => attendanceRows.filter((row) => row.selected).length,
    [attendanceRows]
  );

  const hasChanges = useMemo(() => {
    if (attendanceRows.length !== baselineRows.length) return true;
    const baselineMap = new Map(baselineRows.map((row) => [row.studentId, row]));
    return attendanceRows.some((row) => {
      const base = baselineMap.get(row.studentId);
      if (!base) return true;
      return base.selected !== row.selected || base.status !== row.status || base.notes !== row.notes;
    });
  }, [attendanceRows, baselineRows]);

  const saveBatchAttendance = () => {
    if (!selectedProgramme) return;
    if (!isAdmin && selectedProgramme.staff?.toLowerCase() !== currentUserName.toLowerCase()) {
      setBatchMessage('You only have access to mark attendance for your assigned programme.');
      return;
    }
    const updates = attendanceRows.filter((row) => row.selected);
    if (!updates.length) {
      setBatchMessage('Select at least one student to save attendance.');
      return;
    }
    updates.forEach((row) => {
      const student = students.find((item) => item.id === row.studentId);
      if (!student) return;
      const log = {
        id: `attlog-${Math.random().toString(36).slice(2, 9)}`,
        date: sessionDate,
        recordedAt: new Date().toISOString(),
        status: row.status,
        programme: selectedProgramme.name,
        notes: row.notes,
        recordedBy: currentUserName || 'System'
      };
      const current =
        (student.attendance?.toJSON && student.attendance.toJSON()) || student.attendance || {};
      const logs = [log, ...(current.logs || [])];
      controller.updateStudent(student.id, { attendance: { ...current, logs } }, user);
    });
    setBatchMessage(`Saved attendance for ${updates.length} students.`);
    setBaselineRows(attendanceRows);
  };

  const filteredStudents = useMemo(() => {
    if (!activeKpi) return enrolledStudents;
    if (activeKpi === 'below90') {
      return enrolledStudents.filter((student) => (student.attendance?.rate || 0) < 90);
    }
    if (activeKpi === 'below70') {
      return enrolledStudents.filter((student) => (student.attendance?.rate || 0) < 70);
    }
    if (activeKpi === 'flagged') {
      return enrolledStudents.filter((student) => isSafeguardingFlagged(student));
    }
    return enrolledStudents;
  }, [activeKpi, enrolledStudents]);

  const filteredStudentIds = useMemo(() => new Set(filteredStudents.map((s) => s.id)), [filteredStudents]);

  const rowById = useMemo(() => {
    const map = new Map();
    attendanceRows.forEach((row) => map.set(row.studentId, row));
    return map;
  }, [attendanceRows]);

  const visibleRows = useMemo(
    () => filteredStudents.map((student) => ({ student, row: rowById.get(student.id) })),
    [filteredStudents, rowById]
  );

  const allLogs = useMemo(() => {
    const visibleProgrammes = new Set(programmeOptions.map((prog) => prog.name));
    return students
      .flatMap((student) =>
        (student.attendance?.logs || []).map((log) => ({
          ...log,
          studentId: student.id,
          studentName: student.name,
          status: normalizeAttendanceStatus(log.status)
        }))
      )
      .filter((log) => (isAdmin ? true : visibleProgrammes.has(log.programme)))
      .sort((a, b) => {
        const aTime = new Date(a.recordedAt || a.date).getTime();
        const bTime = new Date(b.recordedAt || b.date).getTime();
        if (Number.isNaN(aTime) || Number.isNaN(bTime)) return 0;
        return bTime - aTime;
      });
  }, [students, programmeOptions, isAdmin]);

  const filteredLogs = useMemo(() => {
    const base = showAttendedLogs ? allLogs : allLogs.filter((log) => isExceptionStatus(log.status));
    const scoped = activeKpi ? base.filter((log) => filteredStudentIds.has(log.studentId)) : base;
    return showAllLogs ? scoped : scoped.slice(0, 12);
  }, [allLogs, showAttendedLogs, showAllLogs, activeKpi, filteredStudentIds]);

  const groupedLogs = useMemo(() => {
    return filteredLogs.reduce((acc, log) => {
      const dateKey = log.date || (log.recordedAt ? log.recordedAt.slice(0, 10) : 'Unknown');
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(log);
      return acc;
    }, {});
  }, [filteredLogs]);

  const toggleKpi = (kpiId) => {
    setActiveKpi((prev) => (prev === kpiId ? null : kpiId));
  };

  const isWithinRange = (value, range) => {
    if (!value) return false;
    const date = new Date(value);
    if (Number.isNaN(date.valueOf())) return false;
    const start = range.start ? new Date(range.start) : null;
    const end = range.end ? new Date(range.end) : null;
    if (start && date < start) return false;
    if (end) {
      const endDate = new Date(end);
      endDate.setHours(23, 59, 59, 999);
      if (date > endDate) return false;
    }
    return true;
  };

  const exportAttendance = () => {
    const selectionSet = new Set(exportStudentIds);
    const rows = allLogs
      .filter((log) => {
        if (!isWithinRange(log.date || log.recordedAt, exportRange)) return false;
        if (exportScope === 'selected' && !selectionSet.has(log.studentId)) return false;
        return true;
      })
      .map((log) => [
        log.studentName,
        log.studentId,
        log.date,
        log.programme,
        getStatusMeta(log.status).label,
        log.notes || ''
      ]);
    downloadCsv('attendance_logs.csv', ['Student', 'ID', 'Date', 'Programme', 'Status', 'Notes'], rows);
    setExportOpen(false);
  };

  const hasProgramme = programmeOptions.length > 0;
  const saveDisabled = !hasProgramme || !selectedCount || !hasChanges;

  return (
    <div className="space-y-6">
      <div className="sp-page-header">
        <div>
          <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>Attendance Workspace</h2>
          <p className="sp-muted">
            Record attendance fast, surface safeguarding signals, and keep logs audit ready.
            {!isAdmin && ' View is limited to your assigned programmes.'}
          </p>
        </div>
        <div className="sp-page-actions">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setShowAllLogs(true);
              setShowAttendedLogs(true);
            }}
            type="button"
          >
            View full logs
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setExportOpen(true)} type="button">
            Export attendance
          </Button>
          <Button size="sm" onClick={() => setConfirmSaveOpen(true)} disabled={saveDisabled} type="button">
            Save attendance
          </Button>
        </div>
      </div>

      <AttendanceKpis
        counts={kpiCounts}
        activeKpi={activeKpi}
        onSelect={toggleKpi}
        threshold={settings.attendanceThreshold}
      />

      <Card className="p-5 sp-attendance-ops">
        <div className="sp-attendance-bar">
          <div className="sp-attendance-bar__left">
            <div>
              <label className="sp-label">Programme</label>
              <select
                className="sp-select mt-2 w-full"
                value={selectedProgrammeId}
                onChange={(event) => setSelectedProgrammeId(event.target.value)}
                disabled={!hasProgramme}
                aria-label="Select programme"
              >
                {programmeOptions.map((programme) => (
                  <option key={programme.id} value={programme.id}>
                    {programme.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="sp-label">Session date</label>
              <DateSelect value={sessionDate} onChange={setSessionDate} className="mt-2 w-full" />
            </div>
            <div>
              <label className="sp-label">Programme info</label>
              <div className="sp-panel sp-panel--muted p-3 mt-2 text-sm">
                <div className="sp-value" style={{ fontWeight: 600 }}>
                  {selectedProgramme?.name || 'Select a programme'}
                </div>
                <div className="text-xs sp-muted mt-1">
                  {selectedProgramme ? `${selectedProgramme.day} · ${selectedProgramme.time}` : 'Schedule not set'}
                </div>
                <div className="text-xs sp-muted mt-1">
                  Staff: {selectedProgramme?.staff || 'Unassigned'}
                </div>
              </div>
            </div>
          </div>
          <div className="sp-attendance-bar__right">
            <Badge variant={isAdmin ? 'info' : 'review'}>
              {isAdmin ? 'Admin access: all programmes' : 'Support worker: assigned programmes'}
            </Badge>
            <div className="sp-attendance-actions">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleMarkAll}
                type="button"
                disabled={!hasProgramme}
              >
                Mark all attended
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => selectAllRows(false)}
                type="button"
                disabled={!hasProgramme}
              >
                Clear selection
              </Button>
              <Button size="sm" onClick={() => setConfirmSaveOpen(true)} disabled={saveDisabled} type="button">
                Save attendance
              </Button>
            </div>
          </div>
        </div>

        {batchMessage && (
          <div className="sp-alert sp-alert--high mt-4 text-sm">{batchMessage}</div>
        )}

        <div className="mt-5">
          {!hasProgramme ? (
            <div className="sp-panel sp-panel--muted p-4 text-sm">
              No programmes assigned. Contact an admin to be assigned to a programme before recording attendance.
            </div>
          ) : enrolledStudents.length === 0 ? (
            <p className="text-sm sp-muted">No students enrolled for this programme yet.</p>
          ) : visibleRows.length === 0 ? (
            <p className="text-sm sp-muted">No students match the selected KPI filter.</p>
          ) : (
            <div className="sp-table sp-table--attendance">
              <div className="sp-row sp-row--header">
                <div>
                  <input
                    type="checkbox"
                    checked={attendanceRows.length > 0 && attendanceRows.every((row) => row.selected)}
                    onChange={(event) => selectAllRows(event.target.checked)}
                    aria-label="Select all students"
                  />
                </div>
                <div>Student</div>
                <div>Attendance</div>
                <div>Status</div>
                <div>Notes</div>
              </div>
              {visibleRows.map(({ student, row }) => {
                if (!row) return null;
                const normalizedStatus = normalizeAttendanceStatus(row.status);
                const statusMeta = getStatusMeta(row.status);
                const isCritical = normalizedStatus === 'unauthorised';
                const isAbsent = normalizedStatus === 'absent';
                const isMuted = normalizedStatus === 'attended';
                const signals = signalsByStudentId.get(student.id) || [];
                const showNotes = notesOpenIds.has(student.id) || Boolean(row.notes);
                return (
                  <React.Fragment key={row.studentId}>
                    {signals.map((signal) => (
                      <div key={signal.id} className="sp-row sp-row--signal">
                        <div style={{ gridColumn: '1 / -1' }}>
                          <div className={`sp-alert ${signal.level === 'critical' ? 'sp-alert--critical' : 'sp-alert--high'} sp-attendance-signal`}>
                            <Icons.Alert />
                            <span>{signal.text}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div
                      className={`sp-row sp-attendance-row ${
                        isCritical ? 'sp-attendance-row--critical' : isAbsent ? 'sp-attendance-row--warn' : ''
                      } ${isMuted ? 'sp-attendance-row--muted' : ''} ${row.selected ? 'sp-row--selected' : ''}`}
                      tabIndex={0}
                    >
                      <div>
                        <input
                          type="checkbox"
                          checked={row.selected}
                          onChange={(event) => updateRow(row.studentId, 'selected', event.target.checked)}
                          aria-label={`Select ${student.name}`}
                        />
                      </div>
                      <div>
                        <div className="sp-value" style={{ fontWeight: 600 }}>{student.name}</div>
                        <div className="text-xs sp-muted">ID: {student.reference}</div>
                        {flaggedStudentIds.has(student.id) && (
                          <div className="sp-attendance-flag">
                            <Icons.Alert />
                            <span>Safeguarding flagged</span>
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="sp-value" style={{ fontWeight: 600 }}>
                          {student.attendance?.rate ?? 0}%
                        </div>
                        <div className="text-xs sp-muted">last 8 weeks</div>
                      </div>
                      <div>
                        <div className={`sp-attendance-status ${isCritical ? 'is-critical' : ''}`}>
                          <Badge variant={statusMeta.variant} className="sp-badge--subtle">
                            {statusMeta.label}
                          </Badge>
                        </div>
                        <select
                          className="sp-select w-full mt-2"
                          value={row.status}
                          onChange={(event) => updateRow(row.studentId, 'status', event.target.value)}
                          aria-label={`Status for ${student.name}`}
                        >
                          {statusOptions.map((status) => (
                            <option key={status.value} value={status.value}>
                              {status.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        {!showNotes ? (
                          <button
                            type="button"
                            className="sp-text-button"
                            onClick={() => toggleNotesOpen(student.id)}
                            aria-label={`Add note for ${student.name}`}
                          >
                            Add note
                          </button>
                        ) : (
                          <textarea
                            className="sp-textarea w-full"
                            rows={2}
                            placeholder="Add a short note"
                            value={row.notes}
                            onChange={(event) => updateRow(row.studentId, 'notes', event.target.value)}
                            onFocus={() => toggleNotesOpen(student.id)}
                            aria-label={`Notes for ${student.name}`}
                          />
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>
      </Card>

      <Card className="p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Recent Attendance Logs</h3>
            <p className="text-sm sp-muted">Exceptions are shown first to reduce noise.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={showAttendedLogs ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setShowAttendedLogs((prev) => !prev)}
            >
              {showAttendedLogs ? 'Hide attended' : 'Show all attended'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAllLogs((prev) => !prev)}
            >
              {showAllLogs ? 'Show less' : 'View full logs'}
            </Button>
          </div>
        </div>
        {filteredLogs.length === 0 ? (
          <p className="text-sm sp-muted">No attendance logs match the selected filters.</p>
        ) : (
          <div className="space-y-4">
            {Object.entries(groupedLogs).map(([date, logs]) => (
              <div key={date}>
                <div className="sp-attendance-log-date">{formatDate(date)}</div>
                <div className="space-y-2">
                  {logs.map((log, idx) => {
                    const statusMeta = getStatusMeta(log.status);
                    return (
                      <div key={`${log.studentId}-${idx}`} className="sp-panel sp-panel--muted p-3 flex items-center justify-between flex-wrap gap-2">
                        <div>
                          <p className="sp-value" style={{ fontWeight: 600 }}>{log.studentName}</p>
                          <p className="text-xs sp-muted">
                            {log.programme ? `${log.programme} · ` : ''}{statusMeta.label}{log.notes ? ` · ${log.notes}` : ''}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={statusMeta.variant}>{statusMeta.label}</Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/students/${log.studentId}`)}
                          >
                            View profile
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <ConfirmDialog
        isOpen={confirmSaveOpen}
        title="Submit attendance"
        description={
          selectedCount
            ? `Submit attendance for ${selectedCount} student${selectedCount === 1 ? '' : 's'} in ${
                selectedProgramme?.name || 'this programme'
              } on ${formatDate(sessionDate)}?`
            : 'Select at least one student to submit attendance.'
        }
        confirmLabel="Submit attendance"
        onConfirm={() => {
          setConfirmSaveOpen(false);
          if (selectedCount) saveBatchAttendance();
        }}
        onCancel={() => setConfirmSaveOpen(false)}
      />

      <ConfirmDialog
        isOpen={confirmBulkOpen}
        title="Confirm mark all attended"
        description={`There ${bulkMeta.flagged === 1 ? 'is' : 'are'} ${bulkMeta.flagged} safeguarding-flagged student${
          bulkMeta.flagged === 1 ? '' : 's'
        } with non-attended statuses. Marking all attended will override them. Unauthorised absences will remain unchanged.`}
        confirmLabel="Override flagged"
        onConfirm={() => {
          setConfirmBulkOpen(false);
          markAllAttended(true);
          if (bulkMeta.unauthorised > 0) {
            setBatchMessage('Unauthorised absences were left unchanged.');
          }
        }}
        onCancel={() => setConfirmBulkOpen(false)}
      />

      <Modal isOpen={exportOpen} onClose={() => setExportOpen(false)}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Export attendance</h3>
              <p className="text-sm sp-muted">Choose a period and the students you want to export.</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setExportOpen(false)}>Close</Button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Period start</label>
              <DateSelect
                value={exportRange.start}
                onChange={(value) => setExportRange((prev) => ({ ...prev, start: value }))}
                className="mt-2 w-full"
              />
            </div>
            <div>
              <label className="sp-label">Period end</label>
              <DateSelect
                value={exportRange.end}
                onChange={(value) => setExportRange((prev) => ({ ...prev, end: value }))}
                className="mt-2 w-full"
              />
            </div>
          </div>

          <div>
            <label className="sp-label">Export scope</label>
            <div className="flex items-center gap-3 mt-2 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="export-scope"
                  value="all"
                  checked={exportScope === 'all'}
                  onChange={() => setExportScope('all')}
                />
                All students in scope
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="export-scope"
                  value="selected"
                  checked={exportScope === 'selected'}
                  onChange={() => setExportScope('selected')}
                />
                Select students
              </label>
            </div>
          </div>

          {exportScope === 'selected' && (
            <div>
              <label className="sp-label">Students</label>
              <SearchableChecklist
                items={scopedStudents}
                selectedIds={exportStudentIds}
                onToggle={(id) =>
                  setExportStudentIds((prev) =>
                    prev.includes(id) ? prev.filter((entry) => entry !== id) : [...prev, id]
                  )
                }
                getId={(student) => student.id}
                getLabel={(student) => student.name}
                getMeta={(student) => student.reference || 'Student'}
                placeholder="Search students"
                emptyLabel="No students available"
              />
            </div>
          )}

          <div className="flex items-center justify-end gap-2">
            <Button variant="secondary" onClick={() => setExportOpen(false)}>Cancel</Button>
            <Button
              onClick={exportAttendance}
              disabled={exportScope === 'selected' && exportStudentIds.length === 0}
            >
              Export CSV
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
